// The home page's photos (src/assets/home, made by scripts/brand-assets/home-assets.sh; credits in
// src/assets/home/CREDITS.md) and the demo boxes drawn on them. Box = [left, top, width, height] in % of the
// photo, from the design's demo data, so a box sits on the face / bib / thing it frames at any size.

import hiking400 from "@/assets/home/hiking-400.webp";
import hiking800 from "@/assets/home/hiking-800.webp";
import hikingFull from "@/assets/home/hiking-full.webp";
import dog400 from "@/assets/home/dog-400.webp";
import dog800 from "@/assets/home/dog-800.webp";
import train400 from "@/assets/home/train-400.webp";
import train800 from "@/assets/home/train-800.webp";
import cake400 from "@/assets/home/cake-400.webp";
import cake800 from "@/assets/home/cake-800.webp";
import breakfast400 from "@/assets/home/beach-breakfast-400.webp";
import breakfast800 from "@/assets/home/beach-breakfast-800.webp";
import sunset400 from "@/assets/home/sunset-400.webp";
import sunset800 from "@/assets/home/sunset-800.webp";
import run3Mile400 from "@/assets/home/run-3-mile-400.webp";
import run3Mile800 from "@/assets/home/run-3-mile-800.webp";
import run3MileFull from "@/assets/home/run-3-mile-full.webp";
import run800m400 from "@/assets/home/run-800m-400.webp";
import run800m800 from "@/assets/home/run-800m-800.webp";
import runFinish400 from "@/assets/home/run-finish-400.webp";
import runFinish800 from "@/assets/home/run-finish-800.webp";
import runMarker400 from "@/assets/home/run-marker-400.webp";
import runMarker800 from "@/assets/home/run-marker-800.webp";
import runForest400 from "@/assets/home/run-forest-400.webp";
import runForest800 from "@/assets/home/run-forest-800.webp";
import runCanal400 from "@/assets/home/run-canal-400.webp";
import runCanal800 from "@/assets/home/run-canal-800.webp";
import runnerFace from "@/assets/home/runner-969-face.webp";
import { withBase } from "@/lib/basePath";

export type Box = readonly [left: number, top: number, width: number, height: number];

export interface DemoPhoto {
  /** i18n key of the alt text (home.*.alt.<id>). */
  id: string;
  src: string;
  srcSet: string;
  width: number;
  height: number;
  /** Demo key (a chip's id or a bib number) → the box that lights up for it. */
  boxes: Record<string, Box>;
}

const set = (w400: string, w800: string) => `${w400} 400w, ${w800} 800w`;

/** Personal demo: everyday photos; chips People / Dog / Birthday cake / Bicycle / Train. */
export const PERSONAL_PHOTOS: DemoPhoto[] = [
  { id: "hiking", src: hiking400, srcSet: set(hiking400, hiking800), width: 960, height: 640, boxes: { people: [45.6, 14.8, 15.1, 29] } },
  { id: "dog", src: dog400, srcSet: set(dog400, dog800), width: 1024, height: 795, boxes: { dog: [6.8, 25.2, 48.8, 50.3] } },
  { id: "train", src: train400, srcSet: set(train400, train800), width: 1024, height: 678, boxes: { train: [9.8, 16.2, 58.6, 66.4], people: [68.8, 27.3, 4.9, 8.1] } },
  { id: "cake", src: cake400, srcSet: set(cake400, cake800), width: 1024, height: 768, boxes: { cake: [3, 6, 94, 90] } },
  { id: "breakfast", src: breakfast400, srcSet: set(breakfast400, breakfast800), width: 1024, height: 607, boxes: { bicycle: [47.9, 27.2, 10.7, 18.1], people: [21, 28, 6.2, 9.1] } },
  { id: "sunset", src: sunset400, srcSet: set(sunset400, sunset800), width: 960, height: 640, boxes: { people: [72.2, 55, 8, 10.6] } },
];

export const PERSONAL_CHIPS = ["people", "dog", "cake", "bicycle", "train"] as const;
export type PersonalChip = (typeof PERSONAL_CHIPS)[number];

/** Business demo: running events; chips are bib numbers. */
export const BUSINESS_PHOTOS: DemoPhoto[] = [
  { id: "run1", src: run3Mile400, srcSet: set(run3Mile400, run3Mile800), width: 1023, height: 684, boxes: { "969": [43.6, 50, 7.2, 8], "1348": [62.7, 49.6, 7, 7.2] } },
  { id: "run2", src: run800m400, srcSet: set(run800m400, run800m800), width: 1023, height: 684, boxes: {} },
  { id: "run3", src: runFinish400, srcSet: set(runFinish400, runFinish800), width: 1023, height: 684, boxes: { "2221": [52.3, 32.6, 5.2, 5] } },
  { id: "run4", src: runMarker400, srcSet: set(runMarker400, runMarker800), width: 1023, height: 684, boxes: {} },
  { id: "run5", src: runForest400, srcSet: set(runForest400, runForest800), width: 683, height: 1024, boxes: { "3098": [62.9, 36.6, 8, 4.2] } },
  { id: "run6", src: runCanal400, srcSet: set(runCanal400, runCanal800), width: 1023, height: 684, boxes: {} },
];

export const BIBS = ["969", "1348", "2221", "3098"] as const;

/** The two hero photos, with the boxes the design frames on them. */
export const PERSONAL_HERO = {
  src: hiking800,
  srcSet: `${hiking400} 400w, ${hiking800} 800w, ${hikingFull} 960w`,
  width: 960,
  height: 640,
  faces: [
    { name: "Tom", box: [45.6, 14.8, 15.1, 29] as Box, delay: "0s" },
    { name: "Ana", box: [62, 23.4, 15.9, 25] as Box, delay: ".8s" },
  ],
};

export const BUSINESS_HERO = {
  src: run3Mile800,
  srcSet: `${run3Mile400} 400w, ${run3Mile800} 800w, ${run3MileFull} 1023w`,
  width: 1023,
  height: 684,
  faces: [
    { box: [42.6, 26.4, 7.4, 11.2] as Box, delay: "0s" },
    { box: [62.6, 24.4, 6.8, 11.4] as Box, delay: "1s" },
  ],
  bibs: [
    { bib: "969", box: [43.4, 49.6, 7.8, 8.6] as Box, delay: ".5s" },
    { bib: "1348", box: [62.4, 49.2, 7.6, 8] as Box, delay: "1.5s" },
  ],
  face: runnerFace,
};

/** The eagle logo (amber beak, no corner brackets): dark for light backgrounds, white for dark ones. */
export const EAGLE_DARK = withBase("/images/brand/eagle-dark.png");
export const EAGLE_WHITE = withBase("/images/brand/eagle-white.png");

/** The home page's "one engine" cards: a face, a bib and a thing, each framed on its photo. Every card shows a 3:2
 *  window of its photo (`view`, [left, top, width, height] in % of the photo; the whole photo when absent). */
export interface TechPhoto {
  src: string;
  srcSet: string;
  width: number;
  height: number;
  boxes: Box[];
  view?: Box;
}
export const TECH_PHOTOS: Record<"faces" | "bibs" | "things", TechPhoto> = {
  faces: { src: hiking800, srcSet: set(hiking400, hiking800), width: 960, height: 640, boxes: PERSONAL_HERO.faces.map((f) => f.box) },
  // a close-up of the finish photo: bib 2221 (as in the bib demo) and the runners around it
  bibs: { src: runFinish800, srcSet: set(runFinish400, runFinish800), width: 1023, height: 684, boxes: [[52.3, 32.6, 5.2, 5]], view: [37, 17, 35, 35] },
  things: { src: dog800, srcSet: set(dog400, dog800), width: 1024, height: 795, boxes: [[6.8, 25.2, 48.8, 50.3]], view: [0, 7, 100, 85.9] },
};

/** A box on a photo → the same box in a window of it (both in %). */
export const inView = (box: Box, view?: Box): Box =>
  view
    ? [((box[0] - view[0]) / view[2]) * 100, ((box[1] - view[1]) / view[3]) * 100, (box[2] / view[2]) * 100, (box[3] / view[3]) * 100]
    : box;
